// Card 23, heraldry at the capitals (R548, ui-map-interface.md §17.3.23).
//
// A portolan set a crest or a shield beside a royal port; a modern chart
// sets the port symbol and the harbour name. The piece
// puts one of the two beside every faction installation, so a reader knows
// whose water this is at every zoom, in greyscale and on the minimap, and
// knows the mark is an installation and not a hold before the tooltip says
// so.
//
// The historical reading draws the faction's own charge — `FACTIONS[i]
// .charge`, which `data/identity/identity.toml` states — inside a shield,
// with a crest over it. The modern reading draws the port circle, the
// anchor and two lines: the name in Title Case and the sub line in lower
// case (the case rule of R1796).
//
// The installation's own plate is the site layer's, not this piece's. The
// mockup drew one because the card had no site layer under it.

import { chart as S } from '../../../gen/strings';
import { anchorGlyph, charge, shieldOutline } from '../../../render/chart-glyphs';
import { toRoman } from '../../../render/roman';
import { cellCenter, factionColor, factionName, factionOf } from '../../../world';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { faceOf, inkOf } from './shared';

/** One installation, ready to draw. */
interface Seat {
  x: number;
  y: number;
  ink: number;
  /** The faction's charge word, for the historical shield. */
  charge: string;
  /** The faction's full name, for the historical sentence. */
  faction: string;
  /** The installation's own name, for the modern name line. */
  name: string;
  /** Its place among its faction's installations, counted from one. */
  index: number;
}

export class HeraldryPiece implements Piece {
  readonly id = 'heraldry';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn = ['hello', 'sites', 'reading', 'font'] as const;

  /** The seats of this world, in site order. The tests read them. */
  seats: Seat[] = [];

  rebuild(ctx: ChartContext): void {
    // By site id, so the numeral a port wears never moves under a reader
    // when the map's site order changes.
    const bases = [...ctx.state.sites.values()]
      .filter((s) => s.kind === SiteKind.SITE_KIND_BASE)
      .sort((a, b) => a.id - b.id);
    const seen = new Map<number, number>();
    this.seats = bases.map((s) => {
      const index = (seen.get(s.faction) ?? 0) + 1;
      seen.set(s.faction, index);
      const [x, y] = cellCenter(s.cell);
      return {
        x, y,
        ink: factionColor(s.faction),
        charge: factionOf(s.faction)?.charge ?? '',
        faction: factionName(s.faction),
        name: s.name,
        index,
      };
    });
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.heraldry;
    const hist = ctx.reading === 'historical';
    const r = t[ctx.reading];
    const face = faceOf(ctx.reading);
    for (const seat of this.seats) {
      const ink = inkOf(r.ink, seat.ink);
      const sx = seat.x + t.fig.dx, sy = seat.y + t.fig.dy;
      const lx = sx + t.fig.label_dx;
      if (hist) {
        const f = t.historical.fig;
        shieldOutline(sinks.segments, sx, sy, f.shield_w, f.shield_h, ink, t.fig.alpha, r.width);
        charge(seat.charge, sinks.segments, sinks.circles, sx, sy, f.shield_w,
          ink, t.fig.alpha, r.width);
        // The crest: three points over the chief, the middle one taller,
        // standing on a bar.
        for (let k = -1; k <= 1; k++) {
          const top = sy - f.crest_top - (k === 0 ? f.crest_peak : 0);
          sinks.segments.line(
            sx + k * f.crest_gap, sy - f.crest_base, sx + k * f.crest_gap, top,
            r.width, ink, t.fig.alpha,
          );
        }
        sinks.segments.line(
          sx - f.crest_w, sy - f.crest_base, sx + f.crest_w, sy - f.crest_base,
          r.width, ink, t.fig.alpha,
        );
        ctx.glyphs.word(
          sinks.quads, S.heraldry_historical({ faction: seat.faction }),
          lx, sy + f.label_dy, face, t.text.main, ink, r.alpha,
        );
      } else {
        const f = t.modern.fig;
        sinks.circles.ring(sx, sy, f.port_r, r.width, ink, t.fig.alpha);
        anchorGlyph(sinks.segments, sinks.circles, sx, sy + f.anchor_dy,
          ink, t.fig.alpha, f.anchor_scale);
        // The two lines are one block, so they grow about one pivot.
        const pivot = { x: lx, y: sy };
        ctx.glyphs.word(
          sinks.quads, S.heraldry_modern({ name: seat.name, index: toRoman(seat.index) }),
          lx, sy + f.label_dy, face, t.text.main, ink, t.fig.alpha, { pivot },
        );
        ctx.glyphs.word(
          sinks.quads, S.heraldry_sub_modern,
          lx, sy + f.sub_dy, face, t.text.sub, ink, t.fig.sub_alpha, { pivot },
        );
      }
    }
  }
}

/** The registry's entry. */
export const heraldry = new HeraldryPiece();
